import React, { useState } from 'react';

import './AccordanceDetailInfo.scss';
import { useTranslation } from 'react-i18next';
import { AccordanceType } from '../../../../../models/AccordanceType';
import { setLastCommentRead } from '../../../../../api/accordanceApi';
import { isMobile } from '../../../../../services/booleanUtil';
import CustomSelect from '../../../../Util/CustomSelect/CustomSelect';
import SelectOption from '../../../../../models/SelectOption';
import { AccordanceState } from '../../../../../models/AccordanceState';
import { setDefaultValues } from '../AccordanceCard/AccordanceCard';
import AccordanceInfoItem from './AccordanceInfoItem';
import SupervisionActionAndComments from './SupervisionActionAndComments';
import moment from 'moment';
import ChronologyAccordance from '../../../../Util/Modals/AccordanceDetail/ChronologyAccordance';
import { useEventContext } from '../../../../../context/EventContext';
import AccordanceDecisionSection from './AccordanceDecisionSection';
import AccordanceModificationInfoSection from './AccordanceModificationInfoSection';
import { capitalizeFirstLetter } from '../../../../../services/stringUtil';
import { ReactComponent as IconAlertExclamation } from '../../../../../assets/icons/u_alert-exclamacion.svg';

type Props = {
  accordance: any;
  isDraftValid?: boolean;
  changeStatus: Function;
  fromRevision?: boolean;
  onNewValue: Function;
  onNewExpirationDateValue: Function;
  OnNewResponsibleValue: Function;
  close: Function;
  newValue: any;
  submitAccordance: any;
  isSubstantialModification?: boolean;
  divChiefRole?: boolean,
  countryRepRole?: boolean,
  approvedByDivisionChief: boolean,
  approvedByCountryRepresentative: boolean,
  vps_managers?: boolean,
  vpc_managers?: boolean,
  approvedByVPSManager: boolean,
  approvedByVPCManager: boolean,
  onConfirmedModifiedSustantialChange?: (value: boolean | null) => void;
  onProceedModificationChange?: (value: boolean | null) => void;
  onCountTextAreaChange?: (value: number) => void;
  onRejectionJustificationChange?: (value: string) => void;
};

const accordanceStateNames = {
  0: 'accordance_draft',
  1: 'pending_accordance_singular',
  2: 'active_accordance_singular',
  3: 'accomplished_accordance_singular',
  4: 'cancelled_accordance_singular',
  5: 'rejected'
};

const AccordanceDetailInfo = (props: Props) => {
  const { t } = useTranslation();

  const {
    accordance,
    isDraftValid = false,
    changeStatus,
    fromRevision,
    onNewValue,
    onNewExpirationDateValue,
    OnNewResponsibleValue,
    close,
    newValue,
    submitAccordance,
    isSubstantialModification,
    divChiefRole,
    countryRepRole,
    approvedByDivisionChief,
    approvedByCountryRepresentative,
    vps_managers,
    vpc_managers,
    approvedByVPSManager,
    approvedByVPCManager,
    onConfirmedModifiedSustantialChange,
    onProceedModificationChange,
    onCountTextAreaChange,
    onRejectionJustificationChange,
  } = props;

  const {
    accordanceState,
    personInCharge,
    accordanceType,
    id,
    description,
    criticalIssueDescription,
    newExpirationDate,
    accordancesSubstantialFundamentalChanges,
    urlPMB
  } = accordance;

  const { events, validity, getDataEvent } = useEventContext();
  const [confirmedModifiedSustantial, setConfirmedModifiedSustantial] = useState<boolean | null>(null);
  const [proceedModification, setProceedModification] = useState<boolean | null>(null);
  const [countTextArea, setCountTextArea] = useState(0);
  const [rejectionJustification, setRejectionJustification] = useState('');

  const statusList = [
    { id: 3, value: '3', label: t('accomplished_accordance_singular') },
    { id: 4, value: '4', label: t('cancelled_accordance_singular') },
  ];

  const handleStatusChange = (item: SelectOption) => {
    changeStatus(item.id);
  };

  const dateColor = () => {
    let currentDate = new Date();
    let dateToExpire = accordance.newExpirationDate !== null ? accordance.newExpirationDate : accordance.expirationDate;
    let diference = moment(new Date(dateToExpire)).diff(currentDate, 'days');
    if (new Date(dateToExpire) < currentDate) {
      return '#FF0000';
    } else if (diference <= 7) {
      return '#000000';
    } else {
      return '#727e8c';
    }
  };

  const expirationDateToCompare = accordance.newExpirationDate || accordance.expirationDate;
  const expirationDaysDiff = expirationDateToCompare
    ? moment(expirationDateToCompare).startOf('day').diff(moment().startOf('day'), 'days')
    : null;

  const expirationCountdownText =
    expirationDaysDiff === null
      ? ''
      : expirationDaysDiff > 0 && expirationDaysDiff <= 7
        ? `${t('expires_on')} ${expirationDaysDiff} ${expirationDaysDiff === 1 ? t('day') : t('days')}`
        : expirationDaysDiff === 0
          ? `${t('expires_today')}`
          : expirationDaysDiff < 0
            ? `${capitalizeFirstLetter(t('expired'))}`
            : '';

  const hasPendingApproverRole = Boolean(divChiefRole) || Boolean(countryRepRole) || Boolean(vps_managers) || Boolean(vpc_managers);
  const shouldHideSupervisionActionAndComments = accordanceState === AccordanceState.PENDING && Boolean(isSubstantialModification) && hasPendingApproverRole;

  const shouldShowDecisionSection =
    isSubstantialModification &&
    accordanceState === AccordanceState.PENDING &&
    (
      (divChiefRole && !approvedByDivisionChief) ||
      (countryRepRole && !approvedByCountryRepresentative)
    );

  React.useEffect(() => {
    setLastCommentRead(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const accordanceInfo = setDefaultValues(accordance, t);

  return isMobile() ? (
    <div className="accordance-detail-info-container">
      <div className="horizontal-row">
        {accordanceState === AccordanceState.VALID && !fromRevision ? (
          <CustomSelect
            options={statusList}
            handleSelected={handleStatusChange}
            style={setCustomSelectStyle(accordanceType)}
            placeholder={t('active_accordance_singular')}
          />
        ) : (
          <AccordanceInfoItem label={t('accordance_status')} data={t(accordanceStateNames[accordanceState])} />
        )}
      </div>
      <div className="horizontal-row">
        <AccordanceInfoItem label={t('critical_issue')} data={accordanceInfo.criticalIssue.name} />
      </div>
      <div className="horizontal-row">
        <AccordanceInfoItem
          label={t('description_of_the_critical_issue')}
          data={criticalIssueDescription || '-'}
          fullWidth
        />
      </div>

      <div className="horizontal-row">
        <AccordanceInfoItem label={t('due_date')} data={accordanceInfo.expirationDate.dateFormatedDetails} />
      </div>

      <div className="horizontal-row">
        {accordanceType === AccordanceType.Revision && (
          <AccordanceInfoItem label={t('revision_accordance')} data={accordanceInfo.revisionDate.dateFormated} />
        )}
      </div>
      <div className="horizontal-row">
        <AccordanceInfoItem label={t('category')} data={accordanceInfo.criticalIssue.categoryName} />
      </div>
      <div className="horizontal-row">
        <AccordanceInfoItem
          label={t('bid_responsible')}
          data={`${accordanceInfo.personInCharge.name} ${accordanceInfo.personInCharge.lastName}`}
        />
      </div>
      <div className="horizontal-row">
        <AccordanceInfoItem label={t('description')} data={description || '-'} fullWidth />
      </div>
      {(!shouldHideSupervisionActionAndComments && accordanceState !== AccordanceState.FULLFILLED) && <SupervisionActionAndComments accordance={accordance} />}
    </div>
  ) : (
    <div className="accordance-detail-layout">
      <div className="accordance-detail-main-column">
        <div className="accordance-modification-card">
          <div>
            <div className="horizontal-row title-horizontal">{t('general_info').toUpperCase()}</div>
            <div >
              <div className="accordance-modification-grid">
                <div>
                  <AccordanceInfoItem
                    label={t('type_of_accordance')}
                    data={accordanceInfo.accordanceAgreementsType.name}
                  />
                </div>

                {(expirationCountdownText && accordanceState !== AccordanceState.FULLFILLED) && (
                  <div>
                    <p className="accordance-detail-text-sm">{t('due_date')}</p>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                      <p className="accordance-detail-text-md">
                        {accordance.newExpirationDate !== null
                          ? accordanceInfo.newExpirationDate.dateFormated
                          : accordanceInfo.expirationDate.dateFormated}
                      </p>
                      <p className='accordance-count-of-days'>{expirationCountdownText}</p>
                    </div>
                  </div>
                )}

                {(!isSubstantialModification) && (
                  <div>
                    <AccordanceInfoItem
                      label={t('responsible')}
                      data={`${accordanceInfo.personInCharge.name} ${accordanceInfo.personInCharge.lastName}`}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="horizontal-row">
              <AccordanceInfoItem label={t('accordance_description')} data={accordance.description || '-'} />
            </div>

            {(isSubstantialModification) ? (
              <AccordanceModificationInfoSection
                accordancesSubstantialFundamentalChanges={accordancesSubstantialFundamentalChanges}
                urlPMB={urlPMB}
              />
            ) : (
              <>
                <div className="horizontal-row title-horizontal">{t('new_context_of_the_critical_issue_and_root_cause')}</div>

                <div className="horizontal-row">
                  <AccordanceInfoItem label={t('critical_issue')} data={accordanceInfo.criticalIssue.name} />
                </div>

                {accordanceInfo?.linkedProductsAccordance.length !== 0 ? (
                  <div className="container-product">
                    <div className={`item-label-data`}>
                      <span className="item-label">{t('new_linked_product')}</span>
                    </div>
                    {accordanceInfo?.linkedProductsAccordance.map(linked => (
                      <div className={`item-label-data`}>
                        <p className="item-data-detail">{linked.nameProduct}</p>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div className="horizontal-row">
                  <AccordanceInfoItem
                    label={t('new_root_cause_of_critical_issue')}
                    data={accordanceInfo.rootCauseCategory?.name}
                  />
                </div>
                <div className="horizontal-row">
                  <AccordanceInfoItem
                    label={t('description_of_the_critical_issue')}
                    data={accordanceInfo.criticalIssueDescription}
                  />
                </div>

                {/* <RelatedAccordances accordance={accordance} close={close} /> */} {/* TODO: Check this component and remove it */}
              </>
            )}
          </div>
        </div>

        {(!shouldHideSupervisionActionAndComments && accordanceState !== AccordanceState.FULLFILLED) && (
          <SupervisionActionAndComments accordance={accordance} />
        )}

        {accordanceState === AccordanceState.DRAFT && !isDraftValid && accordance.currentUserCanEdit && (
          <div className="accordance-detail-draft-warning">
            <IconAlertExclamation className="accordance-detail-draft-warning-icon" />
            <p className="accordance-detail-draft-warning-text">{t('complete_info_accordance')}</p>
          </div>
        )}

        {shouldShowDecisionSection && (
          <AccordanceDecisionSection
            confirmedModifiedSustantial={confirmedModifiedSustantial}
            proceedModification={proceedModification}
            countTextArea={countTextArea}
            rejectionJustification={rejectionJustification}
            onConfirmedModifiedSustantialChange={value => {
              setConfirmedModifiedSustantial(value);
              onConfirmedModifiedSustantialChange?.(value);
            }}
            onProceedModificationChange={value => {
              setProceedModification(value);
              onProceedModificationChange?.(value);
            }}
            onCountTextAreaChange={value => {
              setCountTextArea(value);
              onCountTextAreaChange?.(value);
            }}
            onRejectionJustificationChange={value => {
              setRejectionJustification(value);
              onRejectionJustificationChange?.(value);
            }}
          />
        )}
      </div >

      <div className="accordance-detail-info-container-right">
        <ChronologyAccordance
          accordance={accordanceInfo}
          log={validity}
        />
      </div>
    </div>
  );
};

export default AccordanceDetailInfo;

const setCustomSelectStyle = accordanceType => {
  return accordanceType === AccordanceType.Revision ? { width: 140, marginRight: 20 } : { width: 270 };
};
